import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '../contexts/AuthContext';
import {
  Send,
  Sparkles,
  Trash2,
  Plus,
  Loader2,
  CheckCircle2,
  XCircle,
  Wrench,
  ShieldAlert,
  MessageSquare,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * AIAssistant — the shared chat shell used by:
 *  - the floating bubble (compact = true, no sidebar)
 *  - the /ai-assistant full page (compact = false, sidebar visible)
 *  - the Admin Panel sidebar embed (compact = true, no sidebar)
 *
 * Reads/writes to /api/ai-agent/*. Admin-only — relies on the JWT in
 * localStorage.token. If the caller isn't an admin, the API returns 403
 * and we render an inline "not authorized" state.
 *
 * Streaming protocol: we POST to /chat and parse text/event-stream chunks.
 * We don't use EventSource because EventSource doesn't support Bearer
 * headers in browsers.
 */
export default function AIAssistant({ compact = false, showSidebar = true }) {
  const { user, token, loading: authLoading } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [messages, setMessages] = useState([]); // [{role, content, tool_call?, tool_result?, pending?}]
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const scrollRef = useRef(null);
  const abortRef = useRef(null);

  const isAdmin = !!user?.is_admin || user?.role === 'admin';

  const loadSessions = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/ai-agent/sessions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSessions(res.data.sessions || []);
    } catch (err) {
      if (err.response?.status === 403) setForbidden(true);
    }
  }, [token]);

  const loadMessages = useCallback(
    async (sessionId) => {
      try {
        const res = await axios.get(
          `${API_URL}/api/ai-agent/sessions/${sessionId}/messages`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const raw = res.data.messages || [];
        const ui = raw.map((m) => ({
          role: m.role,
          content: m.content,
          tool_call: m.tool_call,
          tool_result: m.tool_result,
        }));
        setMessages(ui);
      } catch (err) {
        toast.error('Failed to load chat history');
      }
    },
    [token],
  );

  useEffect(() => {
    if (isAdmin && token) loadSessions();
  }, [isAdmin, token, loadSessions]);

  useEffect(() => {
    if (activeSessionId) loadMessages(activeSessionId);
    else setMessages([]);
  }, [activeSessionId, loadMessages]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isStreaming]);

  const newSession = () => {
    setActiveSessionId(null);
    setMessages([]);
    setPendingAction(null);
  };

  const deleteSession = async (sessionId) => {
    if (!window.confirm('Delete this chat?')) return;
    await axios.delete(`${API_URL}/api/ai-agent/sessions/${sessionId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (activeSessionId === sessionId) newSession();
    loadSessions();
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || isStreaming) return;
    setInput('');
    setIsStreaming(true);
    setMessages((m) => [...m, { role: 'user', content: text }]);
    // Placeholder assistant turn — we mutate this as deltas arrive.
    setMessages((m) => [...m, { role: 'assistant', content: '' }]);

    const ctrl = new AbortController();
    abortRef.current = ctrl;

    try {
      const res = await fetch(`${API_URL}/api/ai-agent/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ session_id: activeSessionId, message: text }),
        signal: ctrl.signal,
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let createdSessionId = activeSessionId;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // Parse SSE messages out of the buffer.
        let idx;
        while ((idx = buffer.indexOf('\n\n')) >= 0) {
          const rawEvent = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          const lines = rawEvent.split('\n');
          let eventName = 'message';
          let dataStr = '';
          for (const line of lines) {
            if (line.startsWith('event:')) eventName = line.slice(6).trim();
            else if (line.startsWith('data:')) dataStr += line.slice(5).trim();
          }
          if (!dataStr) continue;
          let payload;
          try {
            payload = JSON.parse(dataStr);
          } catch {
            continue;
          }

          if (eventName === 'session') {
            createdSessionId = payload.session_id;
            if (!activeSessionId) setActiveSessionId(payload.session_id);
          } else if (eventName === 'delta') {
            setMessages((m) => {
              const next = m.slice();
              const lastIdx = next.length - 1;
              if (next[lastIdx]?.role === 'assistant') {
                next[lastIdx] = {
                  ...next[lastIdx],
                  content: (next[lastIdx].content || '') + payload.text,
                };
              }
              return next;
            });
          } else if (eventName === 'tool_run') {
            setMessages((m) => [
              ...m,
              {
                role: 'tool',
                content: '',
                tool_call: { name: payload.name, args: payload.args },
                tool_result: payload.result,
              },
              // Start a fresh assistant placeholder for what comes next.
              { role: 'assistant', content: '' },
            ]);
          } else if (eventName === 'pending_action') {
            setPendingAction({
              action_id: payload.action_id,
              name: payload.name,
              args: payload.args,
              session_id: createdSessionId,
            });
          } else if (eventName === 'error') {
            toast.error(payload.message || 'Agent error');
          }
        }
      }
      // Tidy up any trailing empty assistant placeholder.
      setMessages((m) => {
        const next = m.slice();
        const last = next[next.length - 1];
        if (last && last.role === 'assistant' && !last.content) {
          next.pop();
        }
        return next;
      });
      loadSessions();
    } catch (err) {
      if (err.name !== 'AbortError') {
        toast.error('Chat failed: ' + (err.message || 'unknown'));
      }
    } finally {
      setIsStreaming(false);
      abortRef.current = null;
    }
  };

  const confirmAction = async (approved) => {
    if (!pendingAction) return;
    const pa = pendingAction;
    setPendingAction(null);
    try {
      const res = await axios.post(
        `${API_URL}/api/ai-agent/confirm-action`,
        { session_id: pa.session_id, action_id: pa.action_id, approved },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (approved) {
        toast.success(`Action "${pa.name}" executed`);
        setMessages((m) => [
          ...m,
          {
            role: 'tool',
            content: '',
            tool_call: { name: pa.name, args: pa.args },
            tool_result: res.data.result,
          },
        ]);
      } else {
        toast.info('Action rejected');
        setMessages((m) => [
          ...m,
          {
            role: 'tool',
            content: '',
            tool_call: { name: pa.name, args: pa.args },
            tool_result: { rejected: true },
          },
        ]);
      }
    } catch (err) {
      toast.error('Confirm failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-full p-6 text-slate-400">
        <Loader2 className="animate-spin mr-2" size={18} /> Loading…
      </div>
    );
  }
  if (!isAdmin) {
    return (
      <div className="p-6 text-center text-slate-500">
        <ShieldAlert className="mx-auto mb-3" size={36} />
        <p className="font-medium">AI Copilot is admin-only.</p>
        <p className="text-sm">Sign in with an admin account to use this assistant.</p>
      </div>
    );
  }
  if (forbidden) {
    return (
      <div className="p-6 text-center text-rose-500">
        <ShieldAlert className="mx-auto mb-3" size={36} />
        <p className="font-medium">Access denied (HTTP 403).</p>
      </div>
    );
  }

  return (
    <div className="flex h-full bg-white" data-testid="ai-assistant-root">
      {/* Sidebar */}
      {showSidebar && !compact && (
        <aside className="w-64 border-r border-slate-200 flex flex-col bg-slate-50">
          <div className="p-3 border-b border-slate-200">
            <button
              onClick={newSession}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium"
              data-testid="ai-new-chat-btn"
            >
              <Plus size={16} /> New chat
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {sessions.length === 0 && (
              <p className="text-xs text-slate-400 px-2 py-4">No chats yet.</p>
            )}
            {sessions.map((s) => (
              <div
                key={s.id}
                onClick={() => setActiveSessionId(s.id)}
                className={`group flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer text-sm ${
                  activeSessionId === s.id
                    ? 'bg-indigo-100 text-indigo-900'
                    : 'hover:bg-slate-100 text-slate-700'
                }`}
                data-testid={`ai-session-${s.id}`}
              >
                <MessageSquare size={14} className="flex-shrink-0 opacity-60" />
                <span className="flex-1 truncate">{s.title || 'New chat'}</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteSession(s.id);
                  }}
                  className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-rose-500 transition-opacity"
                  data-testid={`ai-session-delete-${s.id}`}
                >
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        </aside>
      )}

      {/* Main chat */}
      <main className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white">
            <Sparkles size={16} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-slate-900">AI Admin Copilot</h3>
            <p className="text-xs text-slate-500">Claude Sonnet 4.6 · can take actions with your approval</p>
          </div>
          {compact && (
            <button
              onClick={newSession}
              title="New chat"
              className="text-slate-500 hover:text-indigo-600 p-1"
              data-testid="ai-new-chat-compact"
            >
              <Plus size={18} />
            </button>
          )}
        </header>

        {/* Messages */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto px-4 py-4 space-y-3"
          data-testid="ai-messages-container"
        >
          {messages.length === 0 && !isStreaming && (
            <EmptyState onPick={(q) => setInput(q)} />
          )}
          {messages.map((m, idx) => (
            <MessageBubble key={idx} msg={m} />
          ))}
          {isStreaming && messages[messages.length - 1]?.role !== 'assistant' && (
            <div className="flex items-center gap-2 text-slate-500 text-sm">
              <Loader2 className="animate-spin" size={14} /> Thinking…
            </div>
          )}
          {pendingAction && (
            <PendingActionCard
              action={pendingAction}
              onApprove={() => confirmAction(true)}
              onReject={() => confirmAction(false)}
            />
          )}
        </div>

        {/* Composer */}
        <footer className="border-t border-slate-200 p-3">
          <div className="flex items-end gap-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={
                pendingAction
                  ? 'Confirm or reject the pending action above…'
                  : 'Ask the agent anything — e.g., "show me the latest signups"'
              }
              disabled={!!pendingAction || isStreaming}
              rows={compact ? 1 : 2}
              className="flex-1 resize-none px-3 py-2 text-sm text-slate-900 bg-white placeholder:text-slate-400 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:opacity-50"
              data-testid="ai-input"
            />
            <button
              onClick={sendMessage}
              disabled={!input.trim() || isStreaming || !!pendingAction}
              className="p-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="ai-send-btn"
            >
              {isStreaming ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
            </button>
          </div>
          <p className="mt-1.5 text-[10px] text-slate-400">
            Sensitive actions always require your explicit confirmation.
          </p>
        </footer>
      </main>
    </div>
  );
}

function EmptyState({ onPick }) {
  const samples = [
    'Show platform health stats',
    'List the top 10 newest users',
    'How many bookings are still pending?',
    'List all draft courses',
    'Summarize commissions earned in the last 30 days',
  ];
  return (
    <div className="text-center py-10">
      <Sparkles className="mx-auto text-indigo-400 mb-3" size={32} />
      <h3 className="text-base font-semibold text-slate-900 mb-1">How can I help?</h3>
      <p className="text-xs text-slate-500 mb-4">Try one of these:</p>
      <div className="flex flex-wrap justify-center gap-2 max-w-md mx-auto">
        {samples.map((s) => (
          <button
            key={s}
            onClick={() => onPick(s)}
            className="px-3 py-1.5 bg-slate-100 hover:bg-indigo-100 hover:text-indigo-700 rounded-full text-xs text-slate-700 transition-colors"
            data-testid="ai-sample-prompt"
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

function MessageBubble({ msg }) {
  if (msg.role === 'tool') {
    const result = msg.tool_result || {};
    const isError = result.error || result.rejected;
    return (
      <div
        className={`text-xs border rounded-lg p-3 ${
          isError
            ? 'border-rose-200 bg-rose-50 text-rose-800'
            : 'border-emerald-200 bg-emerald-50 text-emerald-900'
        }`}
        data-testid="ai-tool-result"
      >
        <div className="flex items-center gap-1.5 font-medium mb-1">
          <Wrench size={12} />
          <span>{msg.tool_call?.name || 'tool'}</span>
          {result.rejected && <span className="ml-auto text-rose-600">rejected by user</span>}
        </div>
        <details open>
          <summary className="cursor-pointer text-slate-500 hover:text-slate-700">
            View result ({Object.keys(result).length} fields)
          </summary>
          <pre className="mt-2 overflow-x-auto bg-white/60 rounded p-2 text-[11px] leading-relaxed">
            {JSON.stringify(result, null, 2)}
          </pre>
        </details>
      </div>
    );
  }
  const isUser = msg.role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[85%] rounded-2xl px-4 py-2 text-sm ${
          isUser
            ? 'bg-indigo-600 text-white rounded-br-md'
            : 'bg-slate-100 text-slate-900 rounded-bl-md'
        }`}
        data-testid={isUser ? 'ai-user-msg' : 'ai-assistant-msg'}
      >
        {isUser ? (
          <p className="whitespace-pre-wrap">{msg.content}</p>
        ) : (
          <div className="prose prose-sm max-w-none prose-p:my-1 prose-table:my-2 prose-table:text-xs">
            <ReactMarkdown>{msg.content || ''}</ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  );
}

function PendingActionCard({ action, onApprove, onReject }) {
  return (
    <div
      className="border-2 border-amber-300 bg-amber-50 rounded-xl p-4"
      data-testid="ai-pending-action"
    >
      <div className="flex items-start gap-2 mb-2">
        <ShieldAlert className="text-amber-600 flex-shrink-0 mt-0.5" size={18} />
        <div className="flex-1">
          <p className="text-sm font-semibold text-amber-900">
            The agent wants to run a sensitive action
          </p>
          <p className="text-xs text-amber-800">
            Review and approve to execute. This action will be logged in the audit trail.
          </p>
        </div>
      </div>
      <div className="bg-white border border-amber-200 rounded-lg p-3 mb-3">
        <p className="text-xs font-medium text-slate-500 mb-1">Tool</p>
        <p className="text-sm font-mono text-slate-900 mb-2">{action.name}</p>
        <p className="text-xs font-medium text-slate-500 mb-1">Arguments</p>
        <pre className="text-[11px] bg-slate-50 rounded p-2 overflow-x-auto">
          {JSON.stringify(action.args, null, 2)}
        </pre>
      </div>
      <div className="flex gap-2">
        <button
          onClick={onApprove}
          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700"
          data-testid="ai-approve-action"
        >
          <CheckCircle2 size={16} /> Approve & run
        </button>
        <button
          onClick={onReject}
          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-white border border-slate-300 text-slate-700 text-sm rounded-lg hover:bg-slate-50"
          data-testid="ai-reject-action"
        >
          <XCircle size={16} /> Reject
        </button>
      </div>
    </div>
  );
}
